import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import oilogo from './assets/OILogo.png';
import webinarFeed, { WebinarEventRecord } from './webinarsGenerated';

type DemoEvent = WebinarEventRecord & {
  monthLabel: string;
  displayDate: string;
  compactDate: string;
  timeLabel: string;
  day: number;
  monthIndex: number;
  year: number;
};

type MonthSection = {
  key: string;
  monthLabel: string;
  year: number;
  monthIndex: number;
  events: DemoEvent[];
};

type CalendarCell = {
  key: string;
  day?: number;
  events?: DemoEvent[];
  isOutsideMonth: boolean;
};

type Feature = {
  title: string;
  description: string;
};

const contactLink = 'https://forms.microsoft.com/r/NjMys4JGxh';
const companyLink = 'http://outlookinsight.com';
const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const shortWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const scheduleTimeZone = webinarFeed.displayTimeZone || 'America/Los_Angeles';

const featureHighlights: Feature[] = [
  {
    title: 'Knowledge Interpreter training',
    description:
      'Learn how staff can ask natural-language questions and receive fast, authoritative answers grounded in agency rules, laws, and policy guidance.',
  },
  {
    title: 'SixDot BEP oversight workflows',
    description:
      'See how government teams can manage vendor applications, training progress, licensing, assignments, facilities, vending sites, equipment, and work orders in one place.',
  },
  {
    title: 'Monthly reporting to RSA-15',
    description:
      'Explore how vendor-submitted monthly financial reporting supports annual RSA-15 preparation and federal compliance tracking.',
  },
  {
    title: 'Built for government agencies',
    description:
      'Review a practical webinar program tailored for public-sector leaders evaluating operational improvements, onboarding support, and new system rollouts.',
  },
];

const monthLabelFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: scheduleTimeZone,
  month: 'long',
  year: 'numeric',
});

const displayDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: scheduleTimeZone,
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

const compactDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: scheduleTimeZone,
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

const timeLabelFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: scheduleTimeZone,
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

const numericMonthFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: scheduleTimeZone,
  month: 'numeric',
});

const numericDayFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: scheduleTimeZone,
  day: 'numeric',
});

const numericYearFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: scheduleTimeZone,
  year: 'numeric',
});

const syncTimestampFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: scheduleTimeZone,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

const getMonthKey = (year: number, monthIndex: number) => `${year}-${monthIndex}`;

const getMonthKeyFromTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);
  const year = Number.parseInt(numericYearFormatter.format(date), 10);
  const monthIndex = Number.parseInt(numericMonthFormatter.format(date), 10) - 1;

  if (Number.isNaN(year) || Number.isNaN(monthIndex)) {
    return null;
  }

  return getMonthKey(year, monthIndex);
};

const toDisplayEvent = (event: WebinarEventRecord): DemoEvent => {
  const startDate = new Date(event.startIsoUtc);
  const monthIndex = Number.parseInt(numericMonthFormatter.format(startDate), 10) - 1;
  const year = Number.parseInt(numericYearFormatter.format(startDate), 10);

  return {
    ...event,
    monthLabel: monthLabelFormatter.format(startDate),
    displayDate: displayDateFormatter.format(startDate),
    compactDate: compactDateFormatter.format(startDate),
    timeLabel: timeLabelFormatter.format(startDate),
    day: Number.parseInt(numericDayFormatter.format(startDate), 10),
    monthIndex,
    year,
  };
};

const isEventUnavailable = (event: WebinarEventRecord, nowTimestamp: number) => {
  const eventStartTimestamp = new Date(event.startIsoUtc).getTime();
  return Number.isNaN(eventStartTimestamp) || eventStartTimestamp <= nowTimestamp;
};

const buildMonthSections = (events: WebinarEventRecord[], nowTimestamp: number): MonthSection[] => {
  const sections = new Map<string, MonthSection>();

  events
    .filter((event) => !isEventUnavailable(event, nowTimestamp))
    .sort((left, right) => new Date(left.startIsoUtc).getTime() - new Date(right.startIsoUtc).getTime())
    .map(toDisplayEvent)
    .forEach((event) => {
      const key = getMonthKey(event.year, event.monthIndex);
      const existingSection = sections.get(key);

      if (existingSection) {
        existingSection.events.push(event);
        return;
      }

      sections.set(key, {
        key,
        monthLabel: event.monthLabel,
        year: event.year,
        monthIndex: event.monthIndex,
        events: [event],
      });
    });

  return Array.from(sections.values()).sort((left, right) => {
    if (left.year !== right.year) {
      return left.year - right.year;
    }

    return left.monthIndex - right.monthIndex;
  });
};

const getDefaultMonthKey = (sections: MonthSection[], nowTimestamp: number) => {
  const currentMonthKey = getMonthKeyFromTimestamp(nowTimestamp);

  if (currentMonthKey && sections.some((section) => section.key === currentMonthKey)) {
    return currentMonthKey;
  }

  return sections[0]?.key ?? null;
};

const buildCalendarWeeks = (year: number, monthIndex: number, events: DemoEvent[]): CalendarCell[][] => {
  const eventsByDay = new Map<number, DemoEvent[]>();

  events.forEach((event) => {
    const dayEvents = eventsByDay.get(event.day) || [];
    dayEvents.push(event);
    eventsByDay.set(event.day, dayEvents);
  });

  const firstWeekday = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const cells: CalendarCell[] = Array.from({ length: firstWeekday }, (_, index) => ({
    key: `start-${year}-${monthIndex}-${index}`,
    isOutsideMonth: true,
  }));

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      key: `day-${year}-${monthIndex}-${day}`,
      day,
      events: eventsByDay.get(day),
      isOutsideMonth: false,
    });
  }

  const remainder = cells.length % 7;
  if (remainder !== 0) {
    const trailingCells = 7 - remainder;
    cells.push(
      ...Array.from({ length: trailingCells }, (_, index) => ({
        key: `end-${year}-${monthIndex}-${index}`,
        isOutsideMonth: true,
      }))
    );
  }

  return Array.from({ length: cells.length / 7 }, (_, index) => cells.slice(index * 7, index * 7 + 7));
};

const ExternalLinkHint = () => <span className="visually-hidden"> opens in a new tab.</span>;

const formatSyncTimestamp = (isoTimestamp: string) => {
  const parsedDate = new Date(isoTimestamp);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return syncTimestampFormatter.format(parsedDate);
};

function App() {
  const [isMobileCalendar, setIsMobileCalendar] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.innerWidth < 768;
  });
  const [nowTimestamp, setNowTimestamp] = useState(() => Date.now());
  const [activeCalendarMonthKey, setActiveCalendarMonthKey] = useState<string | null>(null);
  const [expandedScheduleMonthKeys, setExpandedScheduleMonthKeys] = useState<string[]>([]);
  const hasInitializedScheduleExpansion = useRef(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileCalendar(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowTimestamp(Date.now());
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const displayedWeekdays = isMobileCalendar ? shortWeekdays : weekdays;
  const monthSections = useMemo(() => buildMonthSections(webinarFeed.webinars, nowTimestamp), [nowTimestamp]);
  const monthCalendars = useMemo(
    () =>
      monthSections.map((section) => ({
        ...section,
        weeks: buildCalendarWeeks(section.year, section.monthIndex, section.events),
      })),
    [monthSections]
  );
  const defaultMonthKey = useMemo(() => getDefaultMonthKey(monthSections, nowTimestamp), [monthSections, nowTimestamp]);
  const activeCalendarMonth = useMemo(
    () =>
      monthCalendars.find((month) => month.key === activeCalendarMonthKey) ??
      monthCalendars.find((month) => month.key === defaultMonthKey) ??
      monthCalendars[0] ??
      null,
    [activeCalendarMonthKey, defaultMonthKey, monthCalendars]
  );
  const selectedCalendarMonthKey = activeCalendarMonth?.key ?? null;
  const hasUpcomingWebinars = monthSections.length > 0;
  const lastSyncedLabel = useMemo(() => formatSyncTimestamp(webinarFeed.generatedAt), []);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (!defaultMonthKey) {
      setActiveCalendarMonthKey(null);
      return;
    }

    setActiveCalendarMonthKey((currentKey) => {
      if (currentKey && monthSections.some((section) => section.key === currentKey)) {
        return currentKey;
      }

      return defaultMonthKey;
    });
  }, [defaultMonthKey, monthSections]);

  useEffect(() => {
    const validMonthKeys = new Set(monthSections.map((section) => section.key));

    setExpandedScheduleMonthKeys((currentKeys) => {
      const retainedKeys = currentKeys.filter((key) => validMonthKeys.has(key));

      if (!hasInitializedScheduleExpansion.current && defaultMonthKey) {
        hasInitializedScheduleExpansion.current = true;
        return [defaultMonthKey];
      }

      if (retainedKeys.length === 0 && currentKeys.length > 0 && defaultMonthKey) {
        return [defaultMonthKey];
      }

      return retainedKeys;
    });
  }, [defaultMonthKey, monthSections]);

  const toggleScheduleMonth = (monthKey: string) => {
    setExpandedScheduleMonthKeys((currentKeys) =>
      currentKeys.includes(monthKey) ? currentKeys.filter((key) => key !== monthKey) : [...currentKeys, monthKey]
    );
  };

  return (
    <div className="oi_app d-flex flex-column min-vh-100">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>

      <header>
        <nav className="navbar navbar-expand-lg navbar-dark oi-navbar shadow-sm" aria-label="Primary">
          <div className="container py-2">
            <a
              className="navbar-brand d-flex align-items-center gap-3 me-0"
              href={companyLink}
              target="_blank"
              rel="noreferrer"
            >
              <img src={oilogo} alt="Outlook Insight logo" className="oi-brand-logo" />
              <span className="d-flex flex-column">
                <span className="fw-bold">Outlook Insight</span>
                <span className="small text-white-50">Modern Technology™</span>
              </span>
              <ExternalLinkHint />
            </a>

            <div className="ms-lg-auto d-flex flex-wrap align-items-center gap-2 gap-md-3 pt-3 pt-lg-0">
              <a className="nav-link text-white px-0 px-md-2" href="#schedule-section">
                Schedule
              </a>
              <a className="nav-link text-white px-0 px-md-2" href="#calendar-section">
                Calendar
              </a>
              <a className="btn btn-outline-light btn-sm" href={contactLink} target="_blank" rel="noreferrer">
                Contact Us
                <ExternalLinkHint />
              </a>
            </div>
          </div>
        </nav>
      </header>

      <main id="main-content" className="flex-grow-1">
        <section className="py-5">
          <div className="container">
            <div className="row g-4 align-items-stretch">
              <div className="col-xl-7">
                <div className="card border-0 shadow-sm h-100 oi-surface-card">
                  <div className="card-body p-4 p-lg-5">
                    <span className="badge rounded-pill oi-section-badge mb-3">What We Offer</span>
                    <h1 className="display-6 fw-bold text-primary-emphasis mb-3">
                      Explore SixDot BEP and the Knowledge Interpreter
                    </h1>
                    <p className="lead text-body-secondary mb-4">
                      Our webinar series now covers two solutions from <strong>Outlook Insight</strong>: the{' '}
                      <strong>Knowledge Interpreter</strong>, which helps staff find authoritative answers quickly, and{' '}
                      <strong>SixDot BEP</strong>, our new application for government agencies managing blind enterprise
                      program operations, vendor oversight, and reporting.
                    </p>

                    <div className="row g-3">
                      {featureHighlights.map((feature) => (
                        <div className="col-md-6" key={feature.title}>
                          <div className="card h-100 oi-feature-card border-0">
                            <div className="card-body">
                              <h2 className="h5 fw-semibold mb-2">{feature.title}</h2>
                              <p className="mb-0 text-body-secondary">{feature.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* <div className="alert alert-primary mt-4 mb-0" role="note">
                      <h2 className="h5 fw-semibold mb-2">What you&apos;ll see in these sessions</h2>
                      <p className="mb-3">
                        Learn how the Knowledge Interpreter can reduce time spent searching for policy answers, and how
                        SixDot BEP gives agency staff and vendors a shared system for applications, training,
                        licensing and assignment, monthly financial reporting, RSA-15 support, and operational record
                        management across facilities, vending, equipment, and work orders.
                      </p>
                      <div className="d-flex flex-wrap gap-2">
                        <a className="btn btn-outline-primary" href={contactLink} target="_blank" rel="noreferrer">
                          Contact Us
                          <ExternalLinkHint />
                        </a>
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>

              <div className="col-xl-5" id="schedule-section">
                <aside className="card border-0 shadow-sm h-100 oi-surface-card" aria-labelledby="schedule-title">
                  <div className="card-body p-4 p-lg-5">
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                      <div>
                        <p className="text-uppercase fw-semibold text-primary small mb-2">Upcoming live sessions</p>
                        <h2 id="schedule-title" className="h3 mb-1">
                          Webinar Dates
                        </h2>
                        <p className="text-body-secondary mb-0">
                          Live sessions cover the Knowledge Interpreter and SixDot BEP. All times shown in Pacific
                          Time.
                        </p>
                        {lastSyncedLabel ? (
                          <p className="small text-body-secondary mb-0 mt-2">Last refreshed {lastSyncedLabel}.</p>
                        ) : null}
                      </div>
                      <a className="btn btn-outline-secondary btn-sm" href="#calendar-section">
                        Browse Calendar
                      </a>
                    </div>

                    {hasUpcomingWebinars ? (
                      <div className="d-grid gap-3">
                        {monthSections.map((section) => {
                          const isExpanded = expandedScheduleMonthKeys.includes(section.key);
                          const dateCountLabel = `${section.events.length} ${section.events.length === 1 ? 'date' : 'dates'}`;
                          const scheduleListId = `schedule-list-${section.key}`;
                          const scheduleHeadingId = `schedule-heading-${section.key}`;

                          return (
                            <section className="oi-schedule-month" key={section.key} aria-labelledby={scheduleHeadingId}>
                              <h3 id={scheduleHeadingId} className="visually-hidden">
                                {section.monthLabel}
                              </h3>
                              <button
                                className={`oi-schedule-month-toggle${isExpanded ? ' is-expanded' : ''}`}
                                type="button"
                                onClick={() => toggleScheduleMonth(section.key)}
                                aria-expanded={isExpanded}
                                aria-controls={scheduleListId}
                              >
                                <span className="fw-semibold">{section.monthLabel}</span>
                                <span className="d-inline-flex align-items-center gap-2">
                                  <span className="badge rounded-pill oi-schedule-month-count">{dateCountLabel}</span>
                                  <span className="oi-schedule-chevron" aria-hidden="true">
                                    v
                                  </span>
                                </span>
                              </button>
                              <ul
                                id={scheduleListId}
                                className="list-group list-group-flush oi-schedule-list"
                                hidden={!isExpanded}
                              >
                                {section.events.map((event) => {
                                  const unavailable = isEventUnavailable(event, nowTimestamp);

                                  return (
                                    <li className="list-group-item px-0 py-2" key={event.id}>
                                      <div className="d-flex justify-content-between align-items-center gap-2 oi-schedule-date-row">
                                        <div className="min-w-0">
                                          <p className="mb-0 fw-semibold text-body-emphasis oi-schedule-date">
                                            <time dateTime={event.startIsoUtc}>{event.compactDate}</time>
                                          </p>
                                          <p className="mb-0 small text-body-secondary">{event.timeLabel}</p>
                                        </div>
                                        <a
                                          className={`btn btn-primary btn-sm flex-shrink-0${unavailable ? ' disabled' : ''}`}
                                          href={event.registrationUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          aria-disabled={unavailable}
                                          onClick={unavailable ? (clickEvent) => clickEvent.preventDefault() : undefined}
                                          aria-label={`Register for ${event.title} on ${event.displayDate} at ${event.timeLabel}. Opens in a new tab.`}
                                        >
                                          Register
                                        </a>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </section>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="alert alert-secondary border-0" role="status">
                        <h3 className="h6 mb-2">No live webinars are currently published.</h3>
                        <p className="mb-0 text-body-secondary">
                          Check back soon, or contact us to request a tailored walkthrough of the Knowledge Interpreter or SixDot BEP.
                        </p>
                      </div>
                    )}

                    <div className="card bg-body-tertiary border-0 mt-4">
                      <div className="card-body">
                        <h3 className="h6 mb-2">Need a different time?</h3>
                        <p className="small text-body-secondary mb-3">
                          If none of the scheduled sessions work for you, contact us to arrange a tailored webinar for
                          your agency.
                        </p>
                        <a className="btn btn-outline-primary w-100" href={contactLink} target="_blank" rel="noreferrer">
                          Contact Us
                          <ExternalLinkHint />
                        </a>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-5" id="calendar-section" aria-labelledby="calendar-title">
          <div className="container">
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-4">
              <div>
                <p className="text-uppercase fw-semibold text-primary small mb-2">Calendar View</p>
                <h2 id="calendar-title" className="h3 mb-1">
                  Registration Calendar
                </h2>
                <p className="text-body-secondary mb-0">
                  Select a highlighted date to open the registration page for an upcoming Knowledge Interpreter and
                  SixDot BEP webinar.
                </p>
              </div>
              <a className="btn btn-outline-primary" href={companyLink} target="_blank" rel="noreferrer">
                Visit Outlook Insight
                <ExternalLinkHint />
              </a>
            </div>

            {hasUpcomingWebinars ? (
              <>
                {monthCalendars.length > 1 ? (
                  <div className="card border-0 shadow-sm oi-surface-card oi-month-switcher mb-4">
                    <div className="card-body p-3 p-md-4">
                      <p className="text-body-secondary small mb-2">Choose a month:</p>
                      <div className="d-flex flex-wrap gap-2" role="tablist" aria-label="Calendar months">
                        {monthCalendars.map((month) => {
                          const isSelected = month.key === selectedCalendarMonthKey;
                          const dateCountLabel = `${month.events.length} ${month.events.length === 1 ? 'date' : 'dates'}`;

                          return (
                            <button
                              className={`btn oi-month-tab d-inline-flex align-items-center gap-2${isSelected ? ' active' : ''}`}
                              type="button"
                              role="tab"
                              id={`calendar-tab-${month.key}`}
                              aria-selected={isSelected}
                              aria-controls={`calendar-panel-${month.key}`}
                              onClick={() => setActiveCalendarMonthKey(month.key)}
                              key={month.key}
                            >
                              <span>{month.monthLabel}</span>
                              <span className="badge rounded-pill oi-month-tab-count">{dateCountLabel}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}

                {activeCalendarMonth ? (
                  <div className="row g-4">
                    <div
                      className="col-lg-6 w-100"
                      key={activeCalendarMonth.monthLabel}
                      id={`calendar-panel-${activeCalendarMonth.key}`}
                      role="tabpanel"
                      aria-labelledby={
                        monthCalendars.length > 1 ? `calendar-tab-${activeCalendarMonth.key}` : 'calendar-title'
                      }
                    >
                      <div className="card border-0 shadow-sm h-100 oi-surface-card">
                        <div className="card-header bg-white border-0 px-4 pt-4 pb-0">
                          <h3 className="h4 mb-1">{activeCalendarMonth.monthLabel}</h3>
                          <p className="text-body-secondary mb-0">Highlighted dates have an active registration link.</p>
                        </div>
                        <div className="card-body p-4">
                          <div className="table-responsive">
                            <table className="table table-bordered align-middle mb-0 oi-calendar-table">
                              <caption className="visually-hidden">{activeCalendarMonth.monthLabel} webinar calendar</caption>
                              <thead className="table-light">
                                <tr>
                                  {displayedWeekdays.map((weekday, index) => (
                                    <th
                                      scope="col"
                                      key={`${activeCalendarMonth.monthLabel}-${weekdays[index]}`}
                                      aria-label={weekdays[index]}
                                    >
                                      {weekday}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {activeCalendarMonth.weeks.map((week, weekIndex) => (
                                  <tr key={`${activeCalendarMonth.monthLabel}-week-${weekIndex}`}>
                                    {week.map((cell) => {
                                      const hasEvents = Boolean(cell.events && cell.events.length > 0);

                                      return (
                                        <td
                                          key={cell.key}
                                          className={
                                            cell.isOutsideMonth
                                              ? 'oi-calendar-empty'
                                              : `oi-calendar-cell${hasEvents ? ' oi-calendar-event-cell' : ''}`
                                          }
                                        >
                                          {cell.isOutsideMonth ? (
                                            <span className="visually-hidden">No date</span>
                                          ) : hasEvents ? (
                                            <div className="d-flex flex-column align-items-start gap-2">
                                              <span className="oi-calendar-day">{cell.day}</span>
                                              {cell.events?.map((event) => {
                                                const unavailable = isEventUnavailable(event, nowTimestamp);

                                                return (
                                                  <div className="oi-calendar-event w-100" key={event.id}>
                                                    <p className="small fw-semibold mb-2">{event.timeLabel}</p>
                                                    <a
                                                      className={`btn btn-primary btn-sm${unavailable ? ' disabled' : ''}`}
                                                      href={event.registrationUrl}
                                                      target="_blank"
                                                      rel="noreferrer"
                                                      aria-disabled={unavailable}
                                                      onClick={unavailable ? (clickEvent) => clickEvent.preventDefault() : undefined}
                                                      aria-label={`Register for ${event.title} on ${event.displayDate} at ${event.timeLabel}. Opens in a new tab.`}
                                                    >
                                                      Register
                                                    </a>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : (
                                            <span className="oi-calendar-day">{cell.day}</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="card border-0 shadow-sm oi-surface-card">
                <div className="card-body p-4">
                  <p className="mb-0 text-body-secondary">
                    No upcoming webinar dates are currently available in the registration calendar.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="oi-footer py-4 mt-auto">
        <div className="container d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <p className="mb-0"><a href="https://outlookinsight.com" target="_blank" rel="noreferrer">Outlook Insight LLC © {currentYear}</a></p>
        </div>
      </footer>
    </div>
  );
}

export default App;
